import { useEffect, useRef } from 'react'

import type { ItemsWithPagination } from '@inno-sdks/files'
import { useQueryClient } from '@tanstack/react-query'
import { INNOSCRIPTA_INTERNAL_ORGANIZATION_ID } from 'shared/constants/user'
import { useUserContext } from 'shared/hooks/useUserContext'

import { initFilesSdk } from '../api/filesSdk'
import { useAppFolderId } from '../api/hooks/useAppFolderId'
import { FILES_LIST_QUERY_KEY } from '../api/hooks/useFilesList'
import { MY_BILLS_SHORTCUT } from '../constants/myBillsShortcut'
import { hasShortcutWithUrl } from '../utils/hasShortcutWithUrl'
import { provisionDesktopShortcut } from '../utils/provisionDesktopShortcut'

/** Provisions My Bills for every user outside the internal organization. */
export const useProvisionMyBillsShortcut = (
  rootFolderItems?: ItemsWithPagination
) => {
  const { data: appFolderId } = useAppFolderId()
  const { user } = useUserContext()
  const queryClient = useQueryClient()
  const isProvisioningRef = useRef(false)

  useEffect(() => {
    if (
      appFolderId == null ||
      rootFolderItems == null ||
      user?.organization_id == null ||
      user.organization_id === INNOSCRIPTA_INTERNAL_ORGANIZATION_ID ||
      isProvisioningRef.current ||
      hasShortcutWithUrl(rootFolderItems, MY_BILLS_SHORTCUT.shortcutUrl)
    )
      return

    const { filesSdk, success } = initFilesSdk()
    if (!success || !filesSdk) return

    isProvisioningRef.current = true

    const provision = async () => {
      const provisioned = await provisionDesktopShortcut({
        filesSdk,
        appFolderId,
        shortcutKey: MY_BILLS_SHORTCUT.key,
        name: MY_BILLS_SHORTCUT.name,
        shortcutUrl: MY_BILLS_SHORTCUT.shortcutUrl,
        failureEvent: 'desktop.my_bills_shortcut_provision_failed'
      })
      isProvisioningRef.current = false
      if (provisioned === 'created') {
        queryClient.invalidateQueries({ queryKey: [FILES_LIST_QUERY_KEY] })
      }
    }

    provision()
  }, [appFolderId, queryClient, rootFolderItems, user?.organization_id])
}
